import React, { useState, useEffect, useCallback, useMemo } from "react";
import {
  Row,
  Col,
  Input,
  Select,
  Pagination,
  Empty,
  Checkbox,
  Radio,
  Spin,
  Breadcrumb,
  Button,
  Modal,
  Dropdown,
  Menu,
  Table,
  Tooltip,
  message,
} from "antd";
import {
  FolderOutlined,
  FolderAddOutlined,
  HomeOutlined,
  MoreOutlined,
  EditOutlined,
  DeleteOutlined,
  EyeOutlined,
  DownloadOutlined,
  SearchOutlined,
  CloseCircleOutlined,
  UserOutlined,
  ClockCircleOutlined,
  AppstoreOutlined,
  UnorderedListOutlined,
  ShareAltOutlined,
} from "@ant-design/icons";
import { attachment } from "@iso/services/attachment.service";
import { AttachmentTypeOptions } from "@iso/constants/AttachmentType";
import IntlMessages from "@iso/components/utility/intlMessages";
import {
  getFileIcon,
  getFileName,
  formatFileSize,
  formatDateAu,
  formatDateTimeAu,
} from "./fileUtils";

// localStorage keys for the per-user view + sort preferences. Stored at
// module scope so all LibraryTab instances share the same key namespace.
const VIEW_MODE_STORAGE_KEY = "fileBrowser.viewMode";
const SORT_MODE_STORAGE_KEY = "fileBrowser.sortMode";

const SORT_OPTIONS = [
  { value: "updated_desc", label: "Date modified (newest)" },
  { value: "name_asc",     label: "Name (A → Z)" },
  { value: "name_desc",    label: "Name (Z → A)" },
];

const readStorage = (key, fallback) => {
  try {
    const stored = window.localStorage?.getItem(key);
    return stored || fallback;
  } catch (err) {
    return fallback;
  }
};

const writeStorage = (key, value) => {
  try {
    window.localStorage?.setItem(key, value);
  } catch (err) {
    // Quota / private-browsing — ignore, the in-memory state still works.
  }
};

const { Option } = Select;

// Callers pass either a stored attachment type (`pdf`) or an HTML accept
// string (`.pdf,application/pdf`). The list API matches the stored type, so
// an accept string filters every file out while folders still appear.
const toAttachmentType = (accept) => {
  if (typeof accept !== "string" || !accept) {
    return undefined;
  }
  if (AttachmentTypeOptions.some((o) => o.value === accept)) {
    return accept;
  }
  const parts = accept.split(",").map((s) => s.trim().toLowerCase());
  if (parts.some((p) => p === "pdf" || p === ".pdf" || p.includes("pdf"))) {
    return "pdf";
  }
  if (parts.some((p) => p === "image" || p.startsWith("image/") || p.startsWith(".jp") || p === ".png" || p === ".gif" || p === ".webp")) {
    return "image";
  }
  if (parts.some((p) => p === "video" || p.startsWith("video/"))) {
    return "video";
  }
  if (parts.some((p) => p === "audio" || p.startsWith("audio/"))) {
    return "audio";
  }
  return undefined;
};

const LibraryTab = ({
  visible = true,
  accept,
  multiple = false,
  maxCount,
  selectedFiles = [],
  onSelectionChange,
  refreshKey,
  corporateGroupId,
  propertyId,
  onFolderChange,
  selectable = true,
  onFileAction,
  rootFolderId = null,
  // Bulk mode — when true, clicking a card toggles selection rather than preview.
  bulkMode = false,
  bulkSelectedIds = [],
  onBulkSelectionChange,
  // Whether the current user can use the assign-user / set-expiry buttons.
  canManage = false,
}) => {
  const [files, setFiles] = useState([]);
  const [folders, setFolders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [totalItem, setTotalItem] = useState(0);
  const [searchText, setSearchText] = useState("");
  const [search, setSearch] = useState("");
  const [typeFilter, setTypeFilter] = useState(() => toAttachmentType(accept));

  // View + sort state. Persisted per-user via localStorage so the chosen
  // layout sticks across navigations / reloads. Picker mode (selectable)
  // always renders the grid — list-mode hover affordances don't fit the
  // single-pick UX.
  const [viewMode, setViewMode] = useState(() =>
    readStorage(VIEW_MODE_STORAGE_KEY, "grid") === "list" ? "list" : "grid"
  );
  const [sortMode, setSortMode] = useState(() => {
    const stored = readStorage(SORT_MODE_STORAGE_KEY, "updated_desc");
    return SORT_OPTIONS.some((o) => o.value === stored) ? stored : "updated_desc";
  });
  // Both standalone (selectable=false) and picker (selectable=true) modes
  // honour the saved view preference. Picker mode uses Antd Table's native
  // rowSelection (radios for single-pick, checkboxes for multi-pick) so the
  // selection UX is intact in either layout.
  const effectiveViewMode = viewMode;

  // Folder navigation state
  const [currentFolderId, setCurrentFolderId] = useState(rootFolderId);
  const [breadcrumbs, setBreadcrumbs] = useState([]);

  // Sync currentFolderId when rootFolderId changes (e.g. async SDS folder resolution)
  useEffect(() => {
    setCurrentFolderId(rootFolderId);
  }, [rootFolderId]);

  // Create folder modal
  const [createFolderVisible, setCreateFolderVisible] = useState(false);
  const [newFolderName, setNewFolderName] = useState("");
  const [creatingFolder, setCreatingFolder] = useState(false);

  // Rename folder modal
  const [renameFolderVisible, setRenameFolderVisible] = useState(false);
  const [renameFolderId, setRenameFolderId] = useState(null);
  const [renameFolderName, setRenameFolderName] = useState("");
  const [renamingFolder, setRenamingFolder] = useState(false);

  const isTypeLocked = typeof accept === "string";

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const promises = [];

      // Fetch files
      const fileParams = { page, sort: sortMode };
      if (typeFilter) fileParams.type = typeFilter;
      if (search) fileParams.search = search;
      if (corporateGroupId) fileParams.corporate_group_id = corporateGroupId;
      else if (propertyId) fileParams.property_id = propertyId;
      if (currentFolderId) fileParams.folder_id = currentFolderId;
      promises.push(attachment.getList(fileParams));

      // Fetch folders (always available, not just with corporate group)
      if (!search) {
        const folderParams = {};
        if (corporateGroupId) folderParams.corporate_group_id = corporateGroupId;
        else if (propertyId) folderParams.property_id = propertyId;
        if (currentFolderId) folderParams.parent_id = currentFolderId;
        promises.push(attachment.getFolders(folderParams));
      } else {
        promises.push(Promise.resolve(null));
      }

      const [fileData, folderData] = await Promise.all(promises);

      if (fileData) {
        setFiles(fileData.attachments || []);
        setTotalItem(fileData.total_item || 0);
      } else {
        setFiles([]);
        setTotalItem(0);
      }

      if (folderData) {
        setFolders(folderData.folders || []);
        setBreadcrumbs(folderData.breadcrumbs || []);
      } else {
        setFolders([]);
        setBreadcrumbs([]);
      }
    } catch (error) {
      console.error("Failed to fetch data:", error);
      setFiles([]);
      setFolders([]);
    } finally {
      setLoading(false);
    }
  }, [page, typeFilter, search, sortMode, refreshKey, corporateGroupId, propertyId, currentFolderId]);

  useEffect(() => {
    if (visible) {
      fetchData();
    }
  }, [fetchData, visible]);

  // Debounce search input
  useEffect(() => {
    const timer = setTimeout(() => {
      setSearch(searchText);
      setPage(1);
    }, 400);
    return () => clearTimeout(timer);
  }, [searchText]);

  const handleTypeChange = (value) => {
    setTypeFilter(value);
    setPage(1);
  };

  const handleSortChange = useCallback((value) => {
    setSortMode(value);
    setPage(1);
    writeStorage(SORT_MODE_STORAGE_KEY, value);
  }, []);

  const handleViewModeChange = useCallback((e) => {
    const next = e.target.value;
    setViewMode(next);
    writeStorage(VIEW_MODE_STORAGE_KEY, next);
  }, []);

  const handlePageChange = (newPage) => {
    setPage(newPage);
  };

  const isSelected = (file) => {
    return selectedFiles.some((f) => f.id === file.id);
  };

  const handleFileClick = useCallback(
    (file) => {
      const isAlreadySelected = selectedFiles.some((f) => f.id === file.id);
      if (multiple) {
        let newSelection;
        if (isAlreadySelected) {
          newSelection = selectedFiles.filter((f) => f.id !== file.id);
        } else {
          if (maxCount && selectedFiles.length >= maxCount) {
            return;
          }
          newSelection = [...selectedFiles, file];
        }
        onSelectionChange(newSelection);
      } else {
        if (isAlreadySelected) {
          onSelectionChange([]);
        } else {
          onSelectionChange([file]);
        }
      }
    },
    [multiple, maxCount, selectedFiles, onSelectionChange]
  );

  // Folder navigation
  const navigateToFolder = (folderId) => {
    setCurrentFolderId(folderId);
    setPage(1);
    setSearchText("");
    setSearch("");
    onFolderChange && onFolderChange(folderId);
  };

  const navigateToRoot = () => {
    setCurrentFolderId(rootFolderId || null);
    setBreadcrumbs([]);
    setPage(1);
    setSearchText("");
    setSearch("");
    onFolderChange && onFolderChange(rootFolderId || null);
  };

  // Create folder
  const handleCreateFolder = async () => {
    if (!newFolderName.trim()) return;
    setCreatingFolder(true);
    try {
      const data = { name: newFolderName.trim() };
      if (corporateGroupId) data.corporate_group_id = corporateGroupId;
      if (currentFolderId) data.parent_id = currentFolderId;
      const res = await attachment.createFolder(data);
      if (res && res.code === "0000") {
        setCreateFolderVisible(false);
        setNewFolderName("");
        fetchData();
      } else {
        message.error(res?.message || "Failed to create folder");
      }
    } catch (error) {
      message.error("Failed to create folder");
    } finally {
      setCreatingFolder(false);
    }
  };

  // Rename folder
  const handleRenameFolder = async () => {
    if (!renameFolderName.trim() || !renameFolderId) return;
    setRenamingFolder(true);
    try {
      const res = await attachment.updateFolder(renameFolderId, {
        name: renameFolderName.trim(),
      });
      if (res && res.code === "0000") {
        setRenameFolderVisible(false);
        setRenameFolderId(null);
        setRenameFolderName("");
        fetchData();
      } else {
        message.error(res?.message || "Failed to rename folder");
      }
    } catch (error) {
      message.error("Failed to rename folder");
    } finally {
      setRenamingFolder(false);
    }
  };

  // Delete folder
  const handleDeleteFolder = (folderId, folderName) => {
    Modal.confirm({
      title: "Delete Folder",
      content: `Are you sure you want to delete "${folderName}"? Files and subfolders will be moved to the parent folder.`,
      okText: "Delete",
      okType: "danger",
      onOk: async () => {
        try {
          const res = await attachment.deleteFolder(folderId);
          if (res && res.code === "0000") {
            fetchData();
          } else {
            message.error("Failed to delete folder");
          }
        } catch (error) {
          message.error("Failed to delete folder");
        }
      },
    });
  };

  // Memoised pieces of the list view. The columns array and the onRow
  // factory are otherwise rebuilt on every parent render — Antd's Table
  // shallow-compares both, so unstable references trigger an extra
  // reconciliation per render. Stabilise both via useMemo / useCallback.
  const listColumns = useMemo(() => {
    const cols = [
      {
        title: "Name",
        key: "name",
        ellipsis: true,
        render: (_, file) => (
          <div className="file-list-name">
            <span className="file-list-icon">{getFileIcon(file.type)}</span>
            <span className="file-list-name-text" title={getFileName(file)}>
              {getFileName(file)}
            </span>
          </div>
        ),
      },
      {
        title: "Type",
        dataIndex: "type",
        key: "type",
        width: 110,
        render: (type) => (
          <span className="file-type-badge">{type || "—"}</span>
        ),
      },
      {
        title: "Size",
        dataIndex: "size_bytes",
        key: "size",
        width: 100,
        render: (bytes) => formatFileSize(bytes),
      },
      {
        title: "Assigned",
        key: "assignee",
        width: 160,
        render: (_, file) =>
          file.user ? (
            <span
              className="file-meta-chip"
              title={`Assigned to ${file.user.name || ""} ${
                file.user.surname || ""
              }`.trim()}
            >
              <UserOutlined />{" "}
              {(file.user.name || "").trim() ||
                (file.user.surname || "").trim() ||
                "Assigned"}
            </span>
          ) : (
            <span style={{ color: "#999" }}>—</span>
          ),
      },
      {
        title: "Expiry",
        dataIndex: "expiry_date",
        key: "expiry",
        width: 130,
        render: (date) => {
          if (!date) return <span style={{ color: "#999" }}>—</span>;
          // Deliberately uses the UTC-midnight parse — the chip turns red
          // around 10–11am AU time on the expiry date itself, which is
          // acceptable for an "is past" indicator. The displayed string
          // still goes through formatDateAu and is timezone-safe.
          const expired = new Date(date) < new Date();
          const formatted = formatDateAu(date);
          return (
            <span
              className={`file-meta-chip ${expired ? "is-expired" : ""}`}
              title={`Expires ${formatted}`}
            >
              <ClockCircleOutlined /> {formatted}
            </span>
          );
        },
      },
      {
        title: "Modified",
        dataIndex: "updated_at",
        key: "modified",
        width: 160,
        render: (raw) => formatDateTimeAu(raw),
      },
    ];

    if (!selectable && !bulkMode && onFileAction) {
      cols.push({
        title: "",
        key: "actions",
        width: canManage ? 210 : 140,
        align: "right",
        render: (_, file) => (
          <div className="file-list-actions">
            <Button
              type="text"
              size="small"
              icon={<EyeOutlined />}
              onClick={(e) => {
                e.stopPropagation();
                onFileAction("preview", file);
              }}
            />
            {file.full_path && (
              <Button
                type="text"
                size="small"
                icon={<DownloadOutlined />}
                onClick={(e) => {
                  e.stopPropagation();
                  onFileAction("download", file);
                }}
              />
            )}
            <Button
              type="text"
              size="small"
              title="Share with user"
              icon={<ShareAltOutlined />}
              onClick={(e) => {
                e.stopPropagation();
                onFileAction("share", file);
              }}
            />
            {canManage && (
              <Button
                type="text"
                size="small"
                title="Assign user"
                icon={<UserOutlined />}
                onClick={(e) => {
                  e.stopPropagation();
                  onFileAction("assignUser", file);
                }}
              />
            )}
            {canManage && (
              <Button
                type="text"
                size="small"
                title="Set expiry date"
                icon={<ClockCircleOutlined />}
                onClick={(e) => {
                  e.stopPropagation();
                  onFileAction("setExpiry", file);
                }}
              />
            )}
            <Button
              type="text"
              size="small"
              danger
              icon={<DeleteOutlined />}
              onClick={(e) => {
                e.stopPropagation();
                onFileAction("delete", file);
              }}
            />
          </div>
        ),
      });
    }

    return cols;
  }, [selectable, bulkMode, onFileAction, canManage]);

  // List-view row selection. Three cases:
  //   - bulk mode (FileManager / SDS) → checkboxes wired to bulkSelectedIds.
  //   - picker mode (selectable=true) → checkboxes (multi) or radios (single)
  //     wired to selectedFiles via onSelectionChange. Mirrors the grid's
  //     handleFileClick logic, including maxCount.
  //   - standalone, no bulk → no row-selection column.
  const listRowSelection = useMemo(() => {
    if (bulkMode) {
      return {
        selectedRowKeys: bulkSelectedIds,
        onChange: (next) =>
          onBulkSelectionChange && onBulkSelectionChange(next),
      };
    }
    if (selectable) {
      // Precomputed lookup so getCheckboxProps doesn't walk selectedFiles
      // for every visible row each render (PR #376 review S3).
      const selectedIdSet = new Set(selectedFiles.map((f) => f.id));
      const atCap =
        multiple &&
        Boolean(maxCount) &&
        selectedFiles.length >= maxCount;

      return {
        type: multiple ? "checkbox" : "radio",
        selectedRowKeys: selectedFiles.map((f) => f.id),
        // preserveSelectedRowKeys keeps cross-page picks alive when the user
        // hits the column-header Select All on a different page (PR #376
        // review W1). Without it Antd's newKeys for the Select All path
        // contains only the current dataSource's row ids, and the merge
        // below would silently drop everything from earlier pages.
        preserveSelectedRowKeys: true,
        // Antd's onChange hands us the full list of selected keys (newKeys —
        // preserved across pages thanks to both preserveSelectedRowKeys and
        // our controlled selectedRowKeys) plus the matching row objects
        // from the current dataSource only. To preserve cross-page /
        // cross-folder selections we merge: keep previously-selected files
        // whose ids are still in newKeys, and add any newly-checked rows
        // from the current page. Mirrors handleFileClick's mutation pattern.
        onChange: (newKeys, currentPageRows) => {
          if (!onSelectionChange) return;
          const keepIds = new Set(newKeys);
          const carriedOver = selectedFiles.filter((f) => keepIds.has(f.id));
          const carriedIds = new Set(carriedOver.map((f) => f.id));
          const additions = currentPageRows.filter(
            (r) => !carriedIds.has(r.id)
          );
          const merged = [...carriedOver, ...additions];
          if (multiple && maxCount && merged.length > maxCount) return;
          onSelectionChange(merged);
        },
        getCheckboxProps: (file) =>
          atCap && !selectedIdSet.has(file.id) ? { disabled: true } : {},
      };
    }
    return undefined;
  }, [
    bulkMode,
    bulkSelectedIds,
    onBulkSelectionChange,
    selectable,
    multiple,
    maxCount,
    selectedFiles,
    onSelectionChange,
  ]);

  const listOnRow = useCallback(
    (file) => ({
      onClick: () => {
        if (bulkMode) {
          if (!onBulkSelectionChange) return;
          const next = bulkSelectedIds.includes(file.id)
            ? bulkSelectedIds.filter((id) => id !== file.id)
            : [...bulkSelectedIds, file.id];
          onBulkSelectionChange(next);
          return;
        }
        if (selectable) {
          handleFileClick(file);
        } else if (onFileAction) {
          onFileAction("preview", file);
        }
      },
      style: { cursor: "pointer" },
    }),
    [
      bulkMode,
      bulkSelectedIds,
      onBulkSelectionChange,
      selectable,
      onFileAction,
      handleFileClick,
    ]
  );

  const renderListView = () => (
    <Table
      className="file-list-table"
      rowKey="id"
      columns={listColumns}
      dataSource={files}
      pagination={false}
      size="middle"
      rowSelection={listRowSelection}
      onRow={listOnRow}
    />
  );

  const getFolderMenu = (folder) => (
    <Menu>
      {!folder.is_system && (
        <Menu.Item
          key="rename"
          icon={<EditOutlined />}
          onClick={(e) => {
            e.domEvent.stopPropagation();
            setRenameFolderId(folder.id);
            setRenameFolderName(folder.name);
            setRenameFolderVisible(true);
          }}
        >
          Rename
        </Menu.Item>
      )}
      {!folder.is_system && (
        <Menu.Item
          key="delete"
          icon={<DeleteOutlined />}
          danger
          onClick={(e) => {
            e.domEvent.stopPropagation();
            handleDeleteFolder(folder.id, folder.name);
          }}
        >
          Delete
        </Menu.Item>
      )}
    </Menu>
  );

  return (
    <div>
      {/* Breadcrumb navigation */}
      <div className="folder-breadcrumb-bar">
        <Breadcrumb>
          <Breadcrumb.Item>
            <a onClick={navigateToRoot}>
              <HomeOutlined /> All Files
            </a>
          </Breadcrumb.Item>
          {(() => {
            const rootIndex = rootFolderId ? breadcrumbs.findIndex((b) => b.id === rootFolderId) : -1;
            return breadcrumbs
              .filter((_, i) => rootIndex < 0 || i > rootIndex);
          })()
            .map((bc, index, filtered) => (
              <Breadcrumb.Item key={bc.id}>
                {index === filtered.length - 1 ? (
                  bc.name
                ) : (
                  <a onClick={() => navigateToFolder(bc.id)}>{bc.name}</a>
                )}
              </Breadcrumb.Item>
            ))}
        </Breadcrumb>
        <Button
          size="small"
          icon={<FolderAddOutlined />}
          onClick={() => setCreateFolderVisible(true)}
        >
          New Folder
        </Button>
      </div>

      <div className="search-filter-bar">
        <Input
          className="file-search-input"
          placeholder="Search files..."
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          suffix={!searchText && <SearchOutlined style={{ color: "#8F92A1" }} />}
          prefix={
            searchText && (
              <CloseCircleOutlined
                style={{ color: "#8F92A1" }}
                onClick={() => setSearchText("")}
              />
            )
          }
        />
        <Select
          className="sort-select"
          value={sortMode}
          onChange={handleSortChange}
          getPopupContainer={(trigger) => trigger.parentNode}
        >
          {SORT_OPTIONS.map((opt) => (
            <Option key={opt.value} value={opt.value}>
              {opt.label}
            </Option>
          ))}
        </Select>
        <Radio.Group
          className="view-mode-toggle"
          value={effectiveViewMode}
          onChange={handleViewModeChange}
          buttonStyle="solid"
          size="middle"
        >
          <Radio.Button value="grid">
            <Tooltip title="Grid view">
              <AppstoreOutlined />
            </Tooltip>
          </Radio.Button>
          <Radio.Button value="list">
            <Tooltip title="List view">
              <UnorderedListOutlined />
            </Tooltip>
          </Radio.Button>
        </Radio.Group>
        <Select
          className="type-filter"
          placeholder={<IntlMessages id="fileBrowser.filter.allTypes" />}
          value={typeFilter}
          onChange={handleTypeChange}
          allowClear={!isTypeLocked}
          disabled={isTypeLocked}
          getPopupContainer={(trigger) => trigger.parentNode}
        >
          {AttachmentTypeOptions.map((opt) => (
            <Option key={opt.value} value={opt.value}>
              {opt.label}
            </Option>
          ))}
        </Select>
      </div>

      <Spin spinning={loading}>
        <div className="library-grid">
          {/* Folder cards */}
          {folders.length > 0 && (
            <Row gutter={[12, 12]} style={{ marginBottom: 12 }}>
              {folders.map((folder) => (
                <Col key={`folder-${folder.id}`} xs={12} sm={8} md={6} lg={6} xl={4}>
                  <div
                    className="folder-card"
                    onClick={() => navigateToFolder(folder.id)}
                  >
                    {!folder.is_system && (
                      <div className="folder-actions">
                        <Dropdown
                          overlay={getFolderMenu(folder)}
                          trigger={["click"]}
                          placement="bottomRight"
                        >
                          <Button
                            type="text"
                            size="small"
                            icon={<MoreOutlined />}
                            onClick={(e) => e.stopPropagation()}
                          />
                        </Dropdown>
                      </div>
                    )}
                    <div className="folder-icon">
                      <FolderOutlined />
                    </div>
                    <div className="folder-name" title={folder.name}>
                      {folder.name}
                    </div>
                    <div className="folder-meta">
                      {folder.file_count > 0 && (
                        <span>{folder.file_count} file{folder.file_count !== 1 ? "s" : ""}</span>
                      )}
                      {folder.subfolder_count > 0 && (
                        <span>
                          {folder.file_count > 0 ? ", " : ""}
                          {folder.subfolder_count} folder{folder.subfolder_count !== 1 ? "s" : ""}
                        </span>
                      )}
                      {folder.file_count === 0 && folder.subfolder_count === 0 && (
                        <span>Empty</span>
                      )}
                    </div>
                  </div>
                </Col>
              ))}
            </Row>
          )}

          {/* File cards */}
          {files.length > 0 ? (
            effectiveViewMode === "list" ? (
              renderListView()
            ) : (
            <Row gutter={[12, 12]}>
              {files.map((file) => {
                const bulkSelected =
                  bulkMode && bulkSelectedIds.includes(file.id);
                const cardClass = [
                  "file-card",
                  selectable && isSelected(file) ? "selected" : "",
                  bulkSelected ? "bulk-selected" : "",
                ]
                  .filter(Boolean)
                  .join(" ");
                return (
                <Col key={file.id} xs={12} sm={8} md={6} lg={6} xl={4}>
                  <div
                    className={cardClass}
                    onClick={() => {
                      if (bulkMode) {
                        if (!onBulkSelectionChange) return;
                        const next = bulkSelected
                          ? bulkSelectedIds.filter((id) => id !== file.id)
                          : [...bulkSelectedIds, file.id];
                        onBulkSelectionChange(next);
                        return;
                      }
                      if (selectable) {
                        handleFileClick(file);
                      } else if (file.type === "image" && file.full_path) {
                        window.open(file.full_path, "_blank", "noopener,noreferrer");
                      } else if (onFileAction) {
                        onFileAction("preview", file);
                      }
                    }}
                  >
                    {(selectable || bulkMode) && (
                      <div className="selection-indicator">
                        {bulkMode ? (
                          <Checkbox checked={bulkSelected} />
                        ) : multiple ? (
                          <Checkbox checked={isSelected(file)} />
                        ) : (
                          <Radio checked={isSelected(file)} />
                        )}
                      </div>
                    )}
                    {!selectable && !bulkMode && onFileAction && (
                      <div className="file-card-actions">
                        <Button
                          type="text"
                          size="small"
                          icon={<EyeOutlined />}
                          onClick={(e) => {
                            e.stopPropagation();
                            onFileAction("preview", file);
                          }}
                        />
                        {file.full_path && (
                          <Button
                            type="text"
                            size="small"
                            icon={<DownloadOutlined />}
                            onClick={(e) => {
                              e.stopPropagation();
                              onFileAction("download", file);
                            }}
                          />
                        )}
                        <Button
                          type="text"
                          size="small"
                          title="Share with user"
                          icon={<ShareAltOutlined />}
                          onClick={(e) => {
                            e.stopPropagation();
                            onFileAction("share", file);
                          }}
                        />
                        {canManage && (
                          <Button
                            type="text"
                            size="small"
                            title="Assign user"
                            icon={<UserOutlined />}
                            onClick={(e) => {
                              e.stopPropagation();
                              onFileAction("assignUser", file);
                            }}
                          />
                        )}
                        {canManage && (
                          <Button
                            type="text"
                            size="small"
                            title="Set expiry date"
                            icon={<ClockCircleOutlined />}
                            onClick={(e) => {
                              e.stopPropagation();
                              onFileAction("setExpiry", file);
                            }}
                          />
                        )}
                        <Button
                          type="text"
                          size="small"
                          icon={<DeleteOutlined />}
                          danger
                          onClick={(e) => {
                            e.stopPropagation();
                            onFileAction("delete", file);
                          }}
                        />
                      </div>
                    )}
                    <div className="file-thumbnail">
                      {file.type === "image" && file.full_path ? (
                        <img src={file.full_path} alt={getFileName(file)} />
                      ) : (
                        getFileIcon(file.type)
                      )}
                    </div>
                    <div className="file-name" title={getFileName(file)}>
                      {getFileName(file)}
                    </div>
                    <div className="file-type-badge">{file.type}</div>
                    {(file.user || file.expiry_date) && (
                      <div className="file-meta-row">
                        {file.user && (
                          <span
                            className="file-meta-chip"
                            title={`Assigned to ${file.user.name || ""} ${file.user.surname || ""}`.trim()}
                          >
                            <UserOutlined />{" "}
                            {(file.user.name || "").trim() ||
                              (file.user.surname || "").trim() ||
                              "Assigned"}
                          </span>
                        )}
                        {file.expiry_date && (
                          <span
                            className={`file-meta-chip ${
                              new Date(file.expiry_date) < new Date()
                                ? "is-expired"
                                : ""
                            }`}
                            title={`Expires ${formatDateAu(file.expiry_date)}`}
                          >
                            <ClockCircleOutlined />{" "}
                            {formatDateAu(file.expiry_date)}
                          </span>
                        )}
                      </div>
                    )}
                  </div>
                </Col>
                );
              })}
            </Row>
            )
          ) : (
            folders.length === 0 &&
            !loading && (
              <Empty
                description={<IntlMessages id="fileBrowser.noFiles" />}
              />
            )
          )}
        </div>
      </Spin>

      {totalItem > 0 && (
        <div className="pagination-bar">
          <Pagination
            current={page}
            total={totalItem}
            pageSize={15}
            onChange={handlePageChange}
            showSizeChanger={false}
            size="small"
          />
        </div>
      )}

      {/* Create Folder Modal */}
      <Modal
        title="New Folder"
        visible={createFolderVisible}
        onOk={handleCreateFolder}
        onCancel={() => {
          setCreateFolderVisible(false);
          setNewFolderName("");
        }}
        confirmLoading={creatingFolder}
        okText="Create"
        zIndex={1200}
      >
        <Input
          placeholder="Folder name"
          value={newFolderName}
          onChange={(e) => setNewFolderName(e.target.value)}
          onPressEnter={handleCreateFolder}
          autoFocus
        />
      </Modal>

      {/* Rename Folder Modal */}
      <Modal
        title="Rename Folder"
        visible={renameFolderVisible}
        onOk={handleRenameFolder}
        onCancel={() => {
          setRenameFolderVisible(false);
          setRenameFolderId(null);
          setRenameFolderName("");
        }}
        confirmLoading={renamingFolder}
        okText="Rename"
        zIndex={1200}
      >
        <Input
          placeholder="Folder name"
          value={renameFolderName}
          onChange={(e) => setRenameFolderName(e.target.value)}
          onPressEnter={handleRenameFolder}
          autoFocus
        />
      </Modal>
    </div>
  );
};

export default LibraryTab;
